import memoizeOne from 'memoize-one';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlaskit/ufo';
import type { OptionData } from '@atlaskit/smart-user-picker';
import { layers } from '@atlaskit/theme/constants';
import type {
	UserWithEmail,
	IntegrationMode,
	User,
	ConfigResponse,
	OriginTracing,
	OriginTracingFactory,
	useFetchSettingsReturnType,
} from './types.tsx';

const ignoredErrorStatusCode = new Set([401, 403, 429]);

export const isValidFailedExperience = (
	experience: UFOExperience,
	// the type for error is 'any' because consumers of the share config can pass in custom error objects
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error: any,
) => {
	const errorStatusCode = error.code || error.status || error.statusCode;

	if (ignoredErrorStatusCode.has(parseInt(errorStatusCode, 10))) {
		experience.abort({
			metadata: {
				error,
			},
		});
		return;
	}

	experience.failure({ metadata: { error } });
};

export const renderShareDialogExp = new UFOExperience('render-share-dialog', {
	type: ExperienceTypes.Experience,
	performanceType: ExperiencePerformanceTypes.InlineResult,
	platform: { component: 'people' },
});

export const shareSubmitExp = new UFOExperience('share-submit-action', {
	type: ExperienceTypes.Experience,
	performanceType: ExperiencePerformanceTypes.InlineResult,
	platform: { component: 'people' },
});

/**
 * We need to generate correct zIndex, for the PopUp and for the Select inside it.
 * The PopUp's defaults to `layers.layer()` from @atlaskit/theme. But if user provides
 * a different value, we need check it, and in case of Select Portal z-index, we need to
 * top it, so it's visible in the popup.
 */

export const zIndexAddition = 10;

export const generateSelectZIndex = (dialogZIndex?: number): number => {
	// If user provided non-number value for zIndex, ignore and return undefined,
	// which will default to `layers.layer()` inside PopUp
	if (typeof dialogZIndex !== 'number' || !dialogZIndex) {
		return layers.modal() + zIndexAddition;
	}
	return dialogZIndex + zIndexAddition;
};

/**
 * Helper to get around TS error of property `current` not being
 * part of React's `ref` object.
 */
export const getMenuPortalTargetCurrentHTML = (
	ref?: React.Ref<HTMLDivElement>,
): HTMLDivElement | null => {
	if (!ref) {
		return null;
	}
	if (!Object.prototype.hasOwnProperty.call(ref, 'current')) {
		return null;
	}

	// @ts-expect-error - Property 'current' does not exist on type '(instance: HTMLDivElement | null) => void'.ts(2339)
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return ref.current as HTMLDivElement | null;
};

export const optionDataToUsers = (optionDataArray: OptionData[]): User[] =>
	optionDataArray.map((optionData: OptionData) => {
		switch (optionData.type) {
			case 'email': {
				const user: UserWithEmail = {
					type: 'user',
					email: optionData.id,
				};
				return user;
			}
			case 'external_user':
				// When optionData.type is 'external_user', we need to convert it to 'user'
				// because 'external_user' isn't a valid type for the '/gateway/api/share' API.
				return {
					type: 'user',
					id: optionData.id,
				};
			default:
				return {
					type: optionData.type || 'user',
					id: optionData.id,
				};
		}
	});

export const allowEmails = (config?: ConfigResponse): boolean =>
	!(config && config.disableSharingToEmails);

export const resolveShareFooter = (
	integrationMode: IntegrationMode | undefined,
	tabIndex: number,
	customFooter: React.ReactNode,
): React.ReactNode | undefined => {
	if (customFooter) {
		if (integrationMode !== 'tabs' || tabIndex === 0) {
			return customFooter;
		}
	}
};

// form origin regenerated after each share
export const getUniqueFormShareOriginTracing = (
	_link: string,
	originTracingFactory: OriginTracingFactory,
): OriginTracing => {
	return originTracingFactory();
};

// ensure origin is re-generated if the link or the factory changes
// separate memoization is needed since copy != form
export const getUniqueCopyLinkOriginTracing = memoizeOne(
	(_link: string, originTracingFactory: OriginTracingFactory): OriginTracing => {
		return originTracingFactory();
	},
);

export const memoizedFormatCopyLink: (origin: OriginTracing, link: string) => string = memoizeOne(
	(origin: OriginTracing, link: string): string => origin.addToUrl(link),
);

export const getIsOutgoingEmailDisabled = (settings: useFetchSettingsReturnType['result']) => {
	if (settings.status === 'success') {
		return settings.payload.emailSettings !== 'error' && !settings.payload.emailSettings?.enabled;
	}
	return false;
};

type SplitUsersResult = {
	usersToInvite: OptionData[];
	usersToShare: OptionData[];
};

export const splitUsers = (users: OptionData[], canInvite: boolean): SplitUsersResult => {
	const initialState: SplitUsersResult = {
		usersToInvite: [],
		usersToShare: [],
	};
	if (!canInvite) {
		return {
			usersToInvite: [],
			usersToShare: users,
		};
	}
	return users.reduce((acc, user) => {
		if (user.type === 'email') {
			acc.usersToInvite.push(user);
		} else {
			acc.usersToShare.push(user);
		}
		return acc;
	}, initialState);
};
